import { get, put } from '../request';

/** 系统级设置（sys_setting 键值；缺失时回落 .env） */
export interface SysSettings {
  ai_max_concurrency?: string;
  /** 智能体函数映射（agent.tool_map，JSON：{"function_name":"api_code"}）：留空=清除键回落内置映射 */
  agent_tool_map?: string;
  /** 同时执行的外呼任务数（调度队列名额，默认 1=严格串行） */
  task_max_concurrency?: string;
  /** AI 呼入并发排队（docs/guangdian/64）：超限行为 wait|transfer|hangup（默认 wait） */
  ai_inbound_overflow_action?: string;
  /** wait 下最长等待秒（默认 60），超时转人工/挂机 */
  ai_inbound_max_wait_sec?: string;
  /** 等待队列上限（默认 20），超出播报后挂机 */
  ai_inbound_max_waiting?: string;
  /** AI 容量排队文案（"当前咨询人数较多，请稍候"） */
  ai_inbound_queue_prompt?: string;
  /** 人工坐席忙文案（"人工客服当前全忙"）——与排队文案区分 */
  ai_inbound_busy_prompt?: string;
  /** 排队安抚语重播间隔秒（0=不重播） */
  ai_inbound_reassure_sec?: string;
  // 读侧键名：GET /sys-settings 原样返回 sys_setting 真实键（点号分组，与 media 读侧常量一致）；
  // 写侧 PUT 入参才是上面的下划线字段名，两者不可混用（见 chat.max_sessions_per_agent 同款）。
  'ai.inbound_overflow_action'?: string;
  'ai.inbound_max_wait_sec'?: string;
  'ai.inbound_max_waiting'?: string;
  'ai.inbound_queue_prompt'?: string;
  'ai.inbound_busy_prompt'?: string;
  'ai.inbound_reassure_sec'?: string;
  /** 统一路由决策点 A：true=语音候选不受在线容量约束（在线打满仍振铃）；false=受约束（默认） */
  voice_ignores_chat?: string;
  /** 在线接待量全局默认（sys_setting 键 chat.max_sessions_per_agent；个人覆盖 chat_max_sessions 优先） */
  'chat.max_sessions_per_agent'?: string;
  media_ws_url?: string;
  fs_domain?: string;
  /** zitch 软电话 WSS 地址（浏览器可达，如 wss://pcc.bcy.sh.cn/zitch-ws）；空=回落构建期 VITE_ZITCH_WSS */
  zitch_wss_url?: string;
  /** SIP/RTP/WS 绑定地址（local_ip_v4）；host 网络下切宿主 IP 后在此修改，留空沿用 FS 容器探测值 */
  fs_local_ip?: string;
  /** 中继（external profile）SIP/RTP 绑定地址（carrier_local_ip）；专线直连时填专线地址，留空=同 fs_local_ip */
  carrier_local_ip?: string;
  /** 中继 SDP/Contact 公告地址（carrier_external_ip）；专线直连时填专线地址，留空=同 fs_domain */
  carrier_external_ip?: string;
  /** 中继信令端口（carrier_sip_port）；留空沿用 FS_SIP_EXT_PORT（默认 5080） */
  carrier_sip_port?: string;
  ai_backchannel_enabled?: string;
  ai_backchannel_words?: string;
  ai_tts_lead_pad_ms?: string;
  ai_tts_trail_pad_ms?: string;
  ai_backchannel_lead_pad_ms?: string;
  ai_backchannel_trail_pad_ms?: string;
  ai_silence_enabled?: string;
  ai_silence_timeout_ms?: string;
  ai_silence_prompt?: string;
  ai_silence_max_prompts?: string;
  ai_silence_goodbye?: string;
  ai_silence_vad_threshold?: string;
  /** barge-in 句末去抖（毫秒；0=关闭）：ASR 新句起音距上句句末不足该值视为尾音残留，不打断播报 */
  ai_barge_debounce_ms?: string;
  /** 两段式打断：客户起音但压音窗口内没有文本证据时先问的一句（空=静默续播） */
  ai_barge_ask_resume?: string;
  /** 两段式打断：明确判为「没听清」时先问的一句（空=直接重播上一段） */
  ai_barge_ask_repeat?: string;
  chat_quality_enabled?: string;
  chat_quality_ratio?: string;
  /** 单通质检报告 AI 分析开关（默认 false）：开启后批量质检运行时逐通生成，消耗 LLM 额度 */
  quality_report_ai_enabled?: string;
  /** 内线互拨录音白名单：分机号逗号分隔（空=内线互拨一律不录音/不质检） */
  intercom_record_exts?: string;
  /** 电话白名单：号码/前缀逗号分隔（空=关闭放行）；渲染进 zitch config/whitelist.json，由引擎执行层双向拦截 */
  outbound_whitelist?: string;
  /** 自适应垫话总开关（默认 false）：答案迟迟未出时按阶梯播报 */
  ai_hold_enabled?: string;
  /** 垫话阶梯 JSON：[{"after_ms":1500,"text":"稍等，我帮您查一下"},…]（空=零行为） */
  ai_hold_templates?: string;
  /** ASR 结果修正：客户句字少时结合上下文经 LLM 纠错（AI 机器人通话） */
  ai_asr_correct_enabled?: string;
  /** ASR 结果修正：纯 WSS 实时语音通道开关 */
  ai_asr_correct_voice?: string;
  /** ASR 结果修正：坐席辅助双轨通道开关 */
  ai_asr_correct_assist?: string;
  /** ASR 结果修正：字阈值（句子字数 ≤ 此值才修正，0=不限制） */
  ai_asr_correct_max_chars?: string;
  /** ASR 结果修正：单句修正超时（毫秒） */
  ai_asr_correct_timeout_ms?: string;
  /** 在线客服话术（正文唯一真源=sys_setting，代码不兜底；缺配置由 config-check 列出） */
  chat_verify_ask_tail?: string;
  chat_verify_retry?: string;
  chat_verify_failed?: string;
  chat_verify_establish?: string;
  chat_verify_needed?: string;
  chat_ask_number?: string;
  chat_query_failed?: string;
  chat_query_empty?: string;
  chat_missing_api?: string;
  chat_transfer?: string;
  /** 查询话术（全局兜底；API 级 data_source_api.querying_text/fallback_text 优先） */
  datasource_querying_text?: string;
  datasource_fallback_text?: string;
  /** 查无该客户（非本网客户）：话术 + 判定关键词（逗号分隔，默认「查询不到,不存在」） */
  datasource_not_customer_text?: string;
  datasource_not_customer_keywords?: string;
  /** 适老慢速播报：总开关（默认 false；会话建立时快照，新会话生效） */
  ai_tts_slow_enabled?: string;
  /** 适老慢速播报：目标倍率 0.5~1.0（按平台可达档位量化后生效，腾讯慢速侧只有 0.8/0.6） */
  ai_tts_slow_ratio?: string;
  /** 适老慢速播报：画像标签命中集（对应 profile.tag_mapping 的标签名，逗号分隔；none=关闭） */
  ai_tts_slow_tags?: string;
  /** 适老慢速播报：本地 customer.tags 关键词（逗号分隔，归一后精确匹配标签；空=不触发） */
  ai_tts_slow_customer_tags?: string;
  /** 适老慢速播报：客户主动表达（听不清/说慢点）触发开关 */
  ai_tts_slow_on_unclear_words?: string;
  /** 适老慢速播报：首次激活时慢速复述最近一句 */
  ai_tts_slow_replay_last?: string;
  /** 适老慢速播报：与机器人级语速的叠加口径 multiply|override|min */
  ai_tts_slow_compose?: string;
  /** 适老慢速播报：连续多少轮未再命中即满足恢复条件（0=只走时间窗） */
  ai_tts_slow_recover_rounds?: string;
  /** 适老慢速播报：距最近一次命中满此时长（秒）且未再命中即恢复 */
  ai_tts_slow_recover_secs?: string;
  /** 适老慢速播报：恢复的最短保持时长（秒） */
  ai_tts_slow_min_hold_secs?: string;
  /** 适老慢速播报：同一通内事件型激活达此次数后升级为整通有效（不再恢复） */
  ai_tts_slow_escalate_activations?: string;
  /** 驾驶舱异常阈值（运营中心 → 驾驶舱预警设置）：接听率下限 0-1（默认 0.7） */
  cockpit_anomaly_answer_rate?: string;
  /** 驾驶舱异常阈值：平均排队等待秒上限（默认 30） */
  cockpit_anomaly_avg_wait?: string;
  /** 驾驶舱异常阈值：满意度均分下限 0-5（默认 4.0） */
  cockpit_anomaly_satisfaction?: string;
  /** 驾驶舱异常阈值：质检均分下限 0-100（默认 80） */
  cockpit_anomaly_quality_score?: string;
  [k: string]: string | undefined;
}

/** 配置缺口（GET /sys-settings/config-check） */
export interface ConfigGap {
  domain: string;
  key: string;
  message: string;
  hint: string;
}

/** 规则词表（GET /sys-settings/rule-words）：kind 元数据 + 当前词条 */
export interface RuleWordItem {
  code: string;
  label: string;
  desc: string;
  consumer: string;
  danger: boolean;
  words: string[];
  count: number;
  /** 词条数上限（后端 ruleword.Kinds 契约；0/缺省 = 后端默认 500） */
  max_words?: number;
  /** 单词最大字符数（0/缺省 = 后端默认 32） */
  word_max_len?: number;
}

export function getRuleWords(): Promise<{ list: RuleWordItem[] }> {
  return get('/sys-settings/rule-words');
}

export function getConfigCheck(): Promise<{ total: number; gaps: ConfigGap[] }> {
  return get('/sys-settings/config-check');
}

export function getSysSettings(): Promise<SysSettings> {
  return get('/sys-settings');
}

/** PUT /sys-settings：字段缺省 = 不修改；空字符串 = 清除（回落 .env） */
export function updateSysSettings(v: Partial<SysSettings>, settings?: Record<string, string>) {
  return put('/sys-settings', settings ? { ...v, settings } : v);
}

/**
 * PUT /sys-settings/rule-words：规则词表页内写（只接受 `ruleword.*` 键，非该前缀键后端 4xx 且不落库）。
 * 与 `PUT /sys-settings` 的 settings 通道同形状，但**不**走全局键值写的敏感能力（syssetting.manage）——
 * 勾了「通用规则词表」页面（页面码 bot.ruleword）即可改词表，不需要额外授予。
 */
export function updateRuleWords(settings: Record<string, string>) {
  return put('/sys-settings/rule-words', { settings });
}

/** 软电话用 FS 域（VITE_SIP_DOMAIN 缺省时经 /sys-settings 动态获取） */
export async function getFsDomain(): Promise<string> {
  try {
    const m = await getSysSettings();
    return m.fs_domain || '';
  } catch {
    return '';
  }
}

/** 短信验证码记录（mock 模式附 mock_code 供演示回显） */
export interface SMSRecord {
  id: number;
  phone_masked: string;
  code_hash: string;
  expire_at: string;
  status: string;
  created_at: string;
  mock_code?: string;
}

export function getSMSRecords(params: { page?: number; pageSize?: number } = {}) {
  return get('/sms-records', params);
}
